import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, UserCheck, Phone, Mail, Home } from 'lucide-react';
import { toast } from 'sonner';
import AddLandlordModal from '../components/modals/AddLandlordModal';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export default function Landlords() {
  const [landlords, setLandlords] = useState([]);
  const [users, setUsers] = useState({});
  const [properties, setProperties] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    loadLandlords();
  }, []);

  const loadLandlords = async () => {
    try {
      const token = localStorage.getItem('token');
      const [landlordsRes, usersRes, propertiesRes] = await Promise.all([
        axios.get(`${API}/landlords`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      setLandlords(landlordsRes.data);
      
      // Create user lookup map
      const userMap = {};
      usersRes.data.forEach(user => {
        userMap[user.id] = user;
      });
      setUsers(userMap);
      
      // Create property lookup map
      const propertyMap = {};
      propertiesRes.data.forEach(property => {
        propertyMap[property.id] = property;
      });
      setProperties(propertyMap);
    } catch (error) {
      toast.error('Failed to load landlords');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div data-testid="landlords-page" className="max-w-7xl mx-auto animate-fade-in">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Landlords</h1>
            <p className="text-gray-600">Manage property owner accounts</p>
          </div>
          <Button data-testid="add-landlord-btn" className="gap-2" onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4" />
            Add Landlord
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : landlords.length === 0 ? (
          <Card data-testid="empty-state">
            <CardContent className="flex flex-col items-center justify-center py-20">
              <UserCheck className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg mb-4">No landlords yet</p>
              <Button data-testid="add-first-landlord-btn" className="gap-2" onClick={() => setShowAddModal(true)}>
                <Plus className="w-4 h-4" />
                Add Your First Landlord
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {landlords.map((landlord) => {
              const user = users[landlord.user_id] || {};
              const propertyCount = landlord.property_ids?.length || 0;
              const landlordProperties = landlord.property_ids?.map(id => properties[id]).filter(Boolean) || [];
              
              return (
                <Card key={landlord.id} data-testid={`landlord-card-${landlord.id}`} className="card-hover">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {user.name?.charAt(0) || 'L'}
                      </div>
                      <div className="flex-1">
                        <div className="text-lg font-semibold text-gray-900">{user.name || 'Landlord'}</div>
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <Home className="w-3 h-3" />
                          {propertyCount} {propertyCount === 1 ? 'property' : 'properties'}
                        </div>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {user.email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-700 truncate">{user.email}</span>
                      </div>
                    )}
                    {user.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-700">{user.phone}</span>
                      </div>
                    )}
                    
                    {/* Property Addresses */}
                    {landlordProperties.length > 0 && (
                      <div className="pt-3 border-t">
                        <p className="text-xs text-gray-500 font-semibold mb-2">Owned Properties</p>
                        <div className="space-y-2">
                          {landlordProperties.map((property, idx) => (
                            <div key={property.id} className="bg-green-50 border border-green-200 rounded p-2">
                              <p className="text-sm font-medium text-gray-900">{property.address}</p>
                              <p className="text-xs text-gray-600">{property.suburb}, {property.state} {property.postcode}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <AddLandlordModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={loadLandlords}
        />
      </div>
    </Layout>
  );
}